import type { DraftState, DraftZone, Position } from '../types';

export const DRAFT_STORAGE_KEY = 'dota2bp:draft:v2';

export const initialDraft: DraftState = {
  mode: 'ranked',
  tier: 'all',
  targetPosition: 1,
  activeZone: 'ally',
  allies: [],
  enemies: [],
  bans: [],
  poolOnly: false,
  personalPool: [],
  positionOverrides: { ally: {}, enemy: {} },
};

export type DraftAction =
  | { type: 'set'; patch: Partial<DraftState> }
  | { type: 'add'; heroId: number; zone?: DraftZone }
  | { type: 'remove'; heroId: number }
  | { type: 'set-position'; heroId: number; side: 'ally' | 'enemy'; position: Position | null }
  | { type: 'toggle-pool'; heroId: number }
  | { type: 'reset-draft' };

export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'set':
      return { ...state, ...action.patch };
    case 'add': {
      if ([...state.allies, ...state.enemies, ...state.bans].includes(action.heroId)) return state;
      const zone = action.zone ?? state.activeZone;
      const key = zone === 'ally' ? 'allies' : zone === 'enemy' ? 'enemies' : 'bans';
      const max = key === 'bans' ? 14 : 5;
      if (state[key].length >= max) return state;
      return { ...state, [key]: [...state[key], action.heroId] };
    }
    case 'remove':
      return {
        ...state,
        allies: state.allies.filter((id) => id !== action.heroId),
        enemies: state.enemies.filter((id) => id !== action.heroId),
        bans: state.bans.filter((id) => id !== action.heroId),
        positionOverrides: {
          ally: withoutKey(state.positionOverrides.ally, action.heroId),
          enemy: withoutKey(state.positionOverrides.enemy, action.heroId),
        },
      };
    case 'set-position': {
      const current = state.positionOverrides[action.side];
      const next = action.position === null
        ? withoutKey(current, action.heroId)
        : { ...current, [String(action.heroId)]: action.position };
      return { ...state, positionOverrides: { ...state.positionOverrides, [action.side]: next } };
    }
    case 'toggle-pool':
      return state.personalPool.includes(action.heroId)
        ? { ...state, personalPool: state.personalPool.filter((id) => id !== action.heroId) }
        : { ...state, personalPool: [...state.personalPool, action.heroId] };
    case 'reset-draft':
      return { ...state, allies: [], enemies: [], bans: [], activeZone: 'ally', positionOverrides: { ally: {}, enemy: {} } };
  }
}

function withoutKey<T>(record: Record<string, T>, key: number): Record<string, T> {
  const next = { ...record };
  delete next[String(key)];
  return next;
}

function migrateDraft(value: unknown): DraftState {
  const stored = value && typeof value === 'object' ? value as Partial<DraftState> : {};
  return {
    ...initialDraft,
    ...stored,
    positionOverrides: {
      ally: { ...(stored.positionOverrides?.ally ?? {}) },
      enemy: { ...(stored.positionOverrides?.enemy ?? {}) },
    },
  };
}

export function loadDraft(storage: Pick<Storage, 'getItem'> | undefined): DraftState {
  if (!storage) return initialDraft;
  try {
    const value = storage.getItem(DRAFT_STORAGE_KEY) ?? storage.getItem('dota2bp:draft:v1');
    return value ? migrateDraft(JSON.parse(value)) : initialDraft;
  } catch {
    return initialDraft;
  }
}

export function saveDraft(storage: Pick<Storage, 'setItem'> | undefined, state: DraftState): void {
  storage?.setItem(DRAFT_STORAGE_KEY, JSON.stringify(state));
}
